/**
 * TodaySnapshot Component
 *
 * Key metrics at a glance:
 * - Today's sales total (£)
 * - Order count today
 * - Today's kg sold
 */

import { useState, useEffect, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { TrendingUp, ShoppingCart, PoundSterling, Weight } from 'lucide-react'
import { LineChart, Line, Tooltip, ResponsiveContainer } from 'recharts'
import { useNavigate } from 'react-router-dom'
import { toLocalDateString } from '../../utils/dateUtils'
import supabase from '../../supabase-client'
import { useSensorData } from '@/hooks/useSensorData'

export default function TodaySnapshot() {
  const navigate = useNavigate()
  const { latest: sensorLatest, history: sensorHistory } = useSensorData('incubation', 'ipac70')
  const incubatorTemp = sensorLatest.temperature?.value ?? null

  const sparklineData = useMemo(() => {
    if (!sensorHistory || sensorHistory.length === 0) return []
    const step = Math.max(1, Math.floor(sensorHistory.length / 60))
    return sensorHistory.filter((_, i) => i % step === 0).map(r => ({ v: r.value }))
  }, [sensorHistory])

  const [loading, setLoading] = useState(true)
  const [metrics, setMetrics] = useState({
    todaySales: 0,
    todayOrders: 0,
    todayKg: 0
  })
  const [sales, setSales] = useState([])

  useEffect(() => {
    fetchMetrics()
  }, [])

  const fetchMetrics = async () => {
    try {
      setLoading(true)

      const today = toLocalDateString(new Date())

      const { data: todaySalesData } = await supabase
        .from('sales')
        .select(`
          id,
          customers ( name, reference_code ),
          branches ( name, reference_code ),
          sale_items (
            quantity,
            price,
            products ( strain )
          )
        `)
        .eq('date', today)

      let todayTotal = 0
      let orderCount = 0
      let totalKg = 0
      const salesList = []

      if (todaySalesData) {
        orderCount = todaySalesData.length
        todaySalesData.forEach(sale => {
          let saleKg = 0
          let saleTotal = 0
          const strains = []

          sale.sale_items?.forEach(item => {
            const lineTotal = (item.quantity || 0) * (item.price || 0)
            saleTotal += lineTotal
            saleKg += item.quantity || 0
            todayTotal += lineTotal
            totalKg += item.quantity || 0
            if (item.products?.strain) {
              strains.push(item.products.strain)
            }
          })

          const custRef = sale.customers?.reference_code || (sale.customers?.name || '').substring(0, 4).toUpperCase()
          const branchRef = sale.branches?.reference_code || sale.branches?.name?.substring(0, 3).toUpperCase()
          const shortRef = branchRef ? `${custRef}-${branchRef}` : custRef

          salesList.push({
            id: sale.id,
            customer: sale.customers?.name || 'Unknown',
            branch: sale.branches?.name || null,
            customerRef: shortRef || 'Unknown',
            products: strains.join(', '),
            kg: saleKg,
            total: saleTotal
          })
        })
      }

      setMetrics({
        todaySales: todayTotal,
        todayOrders: orderCount,
        todayKg: totalKg
      })
      setSales(salesList)
    } catch (error) {
      console.error('Error fetching today metrics:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  const formatCurrencyDetail = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(value)
  }

  const formatKg = (value) => {
    return `${value.toFixed(1)} kg`
  }

  const stats = [
    {
      id: 'sales',
      label: "Today's Sales",
      value: formatCurrency(metrics.todaySales),
      icon: PoundSterling
    },
    {
      id: 'orders',
      label: 'Orders Today',
      value: metrics.todayOrders.toString(),
      icon: ShoppingCart
    },
    {
      id: 'kg',
      label: "Today's kg",
      value: formatKg(metrics.todayKg),
      icon: Weight
    }
  ]

  return (
    <Card className="border-0 shadow-none rounded-none sm:border sm:shadow-sm sm:rounded-xl">
      <CardHeader className="pb-3 px-3 sm:px-6">
        <CardTitle className="text-lg font-medium flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-muted-foreground" />
          Today's Snapshot
          {incubatorTemp !== null && (
            <span className="ml-auto flex items-center gap-2 text-sm font-normal text-muted-foreground">
              <span
                className="cursor-pointer hover:text-foreground transition-colors"
                onClick={() => navigate('/production/incubation')}
              >Incubator: <span className="tabular-nums font-medium text-foreground">{incubatorTemp.toFixed(1)}°C</span></span>
              {sparklineData.length > 1 && (
                <span className="w-16 h-5 inline-block">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={sparklineData}>
                      <Tooltip
                        offset={-10}
                        position={{ y: -35 }}
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null
                          return (
                            <div className="rounded-md bg-white px-2 py-1 text-xs shadow-md border">
                              <span className="font-medium tabular-nums">{payload[0].value.toFixed(1)}°C</span>
                            </div>
                          )
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="v"
                        stroke="hsl(var(--muted-foreground))"
                        strokeWidth={1.5}
                        dot={false}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </span>
              )}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 sm:px-6">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <div className="w-6 h-6 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            {stats.map((stat) => {
              const Icon = stat.icon
              return (
                <div
                  key={stat.id}
                  className="flex flex-col items-center p-2 sm:p-4 rounded-lg bg-muted/30"
                >
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-muted-foreground mb-1 sm:mb-2" />
                  <span className="text-lg sm:text-2xl font-semibold text-foreground">
                    {stat.value}
                  </span>
                  <span className="text-xs sm:text-sm text-muted-foreground text-center">
                    {stat.label}
                  </span>
                </div>
              )
            })}
          </div>

          {/* Sales detail list */}
          {sales.length > 0 && (
            <div className="mt-4 border-t border-border/50 pt-4">
              <div className="grid grid-cols-[1fr_1fr_4rem_5rem] gap-x-3 px-2 pb-2 text-xs text-muted-foreground font-medium">
                <span>Customer</span>
                <span>Products</span>
                <span className="text-right">kg</span>
                <span className="text-right">Cost</span>
              </div>
              {sales.map((sale) => (
                <div
                  key={sale.id}
                  className="grid grid-cols-[1fr_1fr_4rem_5rem] gap-x-3 px-2 py-2 text-sm border-b border-border/50 last:border-b-0 hover:bg-muted/30 transition-colors"
                >
                  <span className="font-medium truncate min-w-0">
                    <span className="landscape:hidden">{sale.customerRef}</span>
                    <span className="hidden landscape:flex landscape:flex-col landscape:leading-tight">
                      <span className="truncate">{sale.customer}</span>
                      {sale.branch && (
                        <span className="text-xs text-muted-foreground font-normal truncate">
                          {sale.branch}
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="text-muted-foreground truncate">{sale.products}</span>
                  <span className="text-right">{sale.kg.toFixed(1)}</span>
                  <span className="text-right font-mono">{formatCurrencyDetail(sale.total)}</span>
                </div>
              ))}
            </div>
          )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
